import React, {useState, useCallback, useEffect} from 'react'
import {toast} from 'react-toastify'
import Icon from '../../core/shared/Icon'
import {StyledSearch, Wrapper} from './CouponSearch.styles'
import {useAuthClient} from '../../utils/use-auth-client'
import {useDispatch} from 'react-redux'
import {fetchCoupons} from '../../app-store/orderSummary/orderSummaryThunk'

/**
 * State-agnostic coupon search with all coupon business rules built in.
 *
 * @param {string}    country         Country code for API calls.
 * @param {string[]}  productCodes    All selected product codes in the cart (for validation + bundle checks).
 * @param {string[]}  [autoCouponProductCodes] Codes newly added in this order (excludes items the account already owns).
 *                                    Auto-apply coupons are looked up against these only, so owning a product
 *                                    (e.g. an existing Marketing Center) doesn't trigger its new-customer incentive.
 *                                    Defaults to productCodes.
 * @param {string}    [thryvId]       Account thryvId. When provided, runs Recurly existing-coupon checks (upgrade path).
 * @param {string}    [clientGroup]   Client group/type for plan coupon filtering (e.g. 'keap'). Defaults to 'null'.
 * @param {boolean}   [applyNewAccountDefaults] Opt-in: also auto-apply the DB-configured default coupons
 *                                    (config_lookup `newAccountDefaultCoupons`, a JSON array of
 *                                    {code, roles, upgradePlans?}) for the caller's roles. Same rule as the
 *                                    legacy Keap catalogs' onboarding-fee waiver (`!isUpgrade || newMC`):
 *                                    always for a new account; for an upgrade only when one of the rule's
 *                                    `upgradePlans` is newly added to the cart. Coupon, roles and plans all
 *                                    come from the DB.
 * @param {boolean}   [isUpgradeJourney] The journey is an upgrade of an existing account (default false).
 * @param {string[]}  [userRoles]     Roles of the current user (only used with applyNewAccountDefaults).
 * @param {object[]}  [initialCoupons] Coupons to pre-populate (e.g. restored from Redux by the parent).
 * @param {Function}  [onCouponsChange] Called with the updated coupons array after every apply / remove.
 */
export function CouponSearch({
  country,
  productCodes = [],
  autoCouponProductCodes = productCodes,
  thryvId,
  isNewCustomer,
  clientGroup,
  applyNewAccountDefaults = false,
  isUpgradeJourney = false,
  userRoles = [],
  initialCoupons = [],
  onCouponsChange,
}) {
  const dispatch = useDispatch()
  const client = useAuthClient()

  const [selectedCoupon, setSelectedCoupon] = useState(null)
  const [applyError, setApplyError] = useState('')
  const [addedCoupons, setAddedCoupons] = useState(initialCoupons)
  const [maxAllowedCoupons, setMaxAllowedCoupons] = useState(0)

  useEffect(() => {
    const init = async () => {
      const response = await client(
        'keappartner/fetchConfigLookupValue/maxAllowedManualCoupons',
      )
      setMaxAllowedCoupons(response?.data[0]?.value)
    }
    init()
  }, [])

  const productCodesString = productCodes.join(',')
  const autoCouponCodesString = autoCouponProductCodes.join(',')

  useEffect(() => {
    if (!country || !productCodes.length) return
    const syncAutoCoupons = async () => {
      try {
        const group = clientGroup || 'null'
        // Nothing new in the cart (all owned): no plan coupons to fetch, and
        // the stale-removal below must still run to drop any auto-coupon.
        const response = autoCouponProductCodes.length
          ? await client(
              `cc/order/fetchPlanCoupons/${country}/${autoCouponCodesString}/${group}/${
                thryvId || 'null'
              }`,
            )
          : []

        let autoApplicable = (response ?? []).filter(coupon => {
          if (coupon.new_client_only_flag === 'Y') {
            // NCI coupons only apply to new customers (account has no paid plans)
            return isNewCustomer && coupon.new_auto_apply === 'Y'
          }
          return (
            coupon.new_auto_apply === 'Y' || coupon.existing_auto_apply === 'Y'
          )
        })

        // Default coupons (e.g. Keap partner onboarding-fee waiver) come from
        // config_lookup so no coupon/role/plan is hardcoded here. Any
        // missing/invalid config just means "no defaults".
        if (applyNewAccountDefaults) {
          try {
            const config = await client(
              'keappartner/fetchConfigLookupValue/newAccountDefaultCoupons',
            )
            const rules = JSON.parse(config?.data?.[0]?.value || '[]')
            const defaultCodes = rules
              .filter(rule =>
                rule?.roles?.some(role => userRoles.includes(role)),
              )
              // New account: always. Upgrade: only if a configured plan was newly added.
              .filter(
                rule =>
                  !isUpgradeJourney ||
                  rule?.upgradePlans?.some(plan =>
                    autoCouponProductCodes.includes(plan),
                  ),
              )
              .map(rule => rule.code)
              .filter(Boolean)
            autoApplicable = [
              ...autoApplicable,
              ...defaultCodes.map(code => ({coupon_code: code})),
            ]
          } catch (err) {
            console.error('newAccountDefaultCoupons', err)
          }
        }

        // In upgrade flow, fetch already-redeemed coupons and filter them out
        // of both the auto-applicable list and any coupons already in state
        // (e.g. restored from a saved proposal as initialCoupons).
        let redeemedCodes = new Set()
        if (thryvId) {
          const redeemedCoupons = await client(
            `accounts/5/coupon-redemptions?thryvId=${thryvId}`,
          )
          redeemedCodes = new Set(redeemedCoupons.map(c => c.code))
          autoApplicable = autoApplicable.filter(
            coupon => !redeemedCodes.has(coupon.coupon_code),
          )
        }

        // Auto-coupon codes that qualify for the current cart
        const autoApplicableCodes = new Set(
          autoApplicable.map(c => c.coupon_code),
        )
        const productCodesSet = new Set(productCodes)

        // Remove stale auto-coupons: either flagged _autoApplied, having
        // appliesToItems with no remaining match in the current cart, or
        // already redeemed on the account (upgrade flow).
        const withoutStale = addedCoupons.filter(c => {
          const code = c.code ?? c.couponCode
          if (redeemedCodes.has(code)) return false
          if (c._autoApplied) return autoApplicableCodes.has(code)
          if (c.appliesToItems?.length > 0) {
            return c.appliesToItems.some(item => productCodesSet.has(item))
          }
          return true
        })

        if (!autoApplicable.length) {
          if (withoutStale.length !== addedCoupons.length) {
            setAddedCoupons(withoutStale)
            onCouponsChange?.(withoutStale)
          }
          return
        }

        const validated = await Promise.all(
          autoApplicable.map(coupon =>
            client(
              `orders/coupon/validate?couponCode=${
                coupon.coupon_code
              }&country=${country}&addonsString=${productCodes.join()}`,
            ).catch(() => null),
          ),
        )

        const newCoupons = validated
          .map((resp, i) =>
            resp?.isValid
              ? {
                  ...resp,
                  _autoApplied: true,
                  incompatibles: autoApplicable[i].incompatibles,
                }
              : null,
          )
          .filter(Boolean)

        // fetchPlanCoupons returns one row per plan_code — dedup by coupon code first
        const uniqueNewCoupons = newCoupons.filter(
          (c, idx, arr) => arr.findIndex(x => x.code === c.code) === idx,
        )

        const dedupedPrev = withoutStale.filter(
          (p, idx, arr) =>
            arr.findIndex(
              x => (x?.code ?? x?.couponCode) === (p?.code ?? p?.couponCode),
            ) === idx,
        )
        const merged = [
          ...dedupedPrev,
          ...uniqueNewCoupons.filter(
            c => !dedupedPrev.find(p => (p?.code ?? p?.couponCode) === c.code),
          ),
        ]
        setAddedCoupons(merged)
        onCouponsChange?.(merged)
      } catch (err) {
        console.error(err)
      }
    }
    syncAutoCoupons()
  }, [
    productCodesString,
    autoCouponCodesString,
    applyNewAccountDefaults,
    isUpgradeJourney,
  ])

  const validateCoupon = useCallback(
    async couponCode => {
      try {
        const url = `orders/coupon/validate?couponCode=${couponCode}&country=${country}&addonsString=${productCodes.join()}`
        return await client(url)
      } catch {
        return {isValid: false}
      }
    },
    [client, country, productCodes],
  )

  const fetchCouponOptions = useCallback(
    async query => {
      const result = await dispatch(
        fetchCoupons({client, country, coupon: query}),
      ).unwrap()
      return result.map(coupon => ({
        // Support both old (coupon_code) and new (code) API field names
        label: coupon.coupon_desc || coupon.coupon_code,
        value: coupon.coupon_code,
        description: coupon.coupon_desc,
        discountLabel: coupon.coupon_desc,
        autoCoupon: coupon?.autoAddCoupons,
        autoCouponProds: coupon?.requiredProducts,
        incompatibles: coupon?.incompatibles,
        bundle_combinations: coupon?.bundle_combinations,
      }))
    },
    [dispatch, client, country],
  )

  const handleSelect = useCallback(opt => {
    setSelectedCoupon(opt)
    setApplyError('')
  }, [])

  const handleClear = useCallback(() => {
    setSelectedCoupon(null)
    setApplyError('')
  }, [])

  const handleApply = useCallback(
    async (_inputValue, selectedOption) => {
      if (!selectedOption) {
        setApplyError('Please select a coupon code from the list.')
        return
      }
      setApplyError('')

      const e = selectedOption
      const isUpgrade = !!thryvId
      const auto_coupon = e.autoCoupon || null
      const auto_coupon_prods =
        e.autoCouponProds?.length > 0 ? e.autoCouponProds.split(',') : null
      const incompatibleCoupons = e.incompatibles?.split(',')

      // Recurly existing-coupon check — upgrade path only
      let recurlyValidationError = false
      if (isUpgrade) {
        const existingCoupons = await client(
          `accounts/5/coupon-redemptions?thryvId=${thryvId}`,
        )
        const activeCouponCodes = existingCoupons.map(c => c.code)

        if (activeCouponCodes.includes(e.value)) {
          toast.error(
            "Oops! Looks like these coupons are already applied to the client's account, so they'll be removed from this order. Don't worry, the right discounts will still apply when their payment is processed",
          )
          recurlyValidationError = true
        }

        if (
          !recurlyValidationError &&
          incompatibleCoupons?.length > 0 &&
          incompatibleCoupons.some(ic => activeCouponCodes.includes(ic))
        ) {
          toast.error(
            'The coupon you are trying to apply can not be combined with an existing coupon on your account. Coupon not applied.',
          )
          recurlyValidationError = true
        }
      }

      if (!recurlyValidationError) {
        // Bundle combination validation
        if (e.bundle_combinations) {
          const matched = e.bundle_combinations.filter(combination =>
            combination.split(',').every(cp => productCodes.includes(cp)),
          )
          if (!matched.length) {
            alert(
              "Oops! Some items in your cart don't qualify for this bundle coupon. Please update your cart to continue.",
            )
          }
        }

        // Skip if already applied
        if (!addedCoupons.find(c => c?.code === e.value)) {
          const resp = await validateCoupon(e.value)
          if (resp.isValid) {
            const newCoupons = [{...resp, incompatibles: e.incompatibles}]
            toast.success('Coupon applied.')

            // Auto-coupon: conditionally add a companion coupon
            if (
              auto_coupon &&
              !addedCoupons.find(c => c?.code === auto_coupon)
            ) {
              const shouldApply =
                !auto_coupon_prods ||
                auto_coupon_prods.some(p => productCodes.includes(p))

              if (shouldApply) {
                const autoResp = await validateCoupon(auto_coupon)
                if (autoResp.isValid) {
                  newCoupons.push({...autoResp, incompatibles: e.incompatibles})
                  toast.success('Coupon applied.')
                } else {
                  toast.error('Invalid coupon.')
                }
              }
            }

            // Remove coupons in the list that are incompatible with the new one
            const removedCodes = addedCoupons
              .filter(c => incompatibleCoupons?.includes(c.code))
              .map(c => c.code)
            if (removedCodes.length > 0) {
              toast.info(
                `${removedCodes.join(', ')} can't be combined with ${
                  e.value
                } and was removed.`,
              )
            }
            const updated =
              incompatibleCoupons?.length > 0
                ? [
                    ...addedCoupons.filter(
                      c => !incompatibleCoupons.includes(c.code),
                    ),
                    ...newCoupons,
                  ]
                : [...addedCoupons, ...newCoupons]

            setAddedCoupons(updated)
            onCouponsChange?.(updated)
            setSelectedCoupon(null)
          } else {
            toast.error('Invalid coupon.')
          }
        }
      }
    },
    [
      addedCoupons,
      thryvId,
      client,
      productCodes,
      validateCoupon,
      onCouponsChange,
    ],
  )

  return (
    <Wrapper>
      {/* FULSAS-13837: the cap is on manual coupons, so auto-applied ones
          (e.g. the Social Ads 75% coupon) must not use up a slot */}
      {!(
        addedCoupons.filter(c => !c._autoApplied).length >= maxAllowedCoupons
      ) && (
        <StyledSearch
          placeholder="Select Coupon Code"
          fetchOptions={fetchCouponOptions}
          debounceDelay={400}
          minCharsToFetch={3}
          loadingMessage="Searching coupons…"
          emptyMessage="No matching coupon codes."
          selectedValue={selectedCoupon?.value}
          onSelect={handleSelect}
          clearable
          onClear={handleClear}
          actionIcon={<Icon name="promo-code" size={18} />}
          actionLabel="Apply"
          onAction={handleApply}
          actionDisabled={!selectedCoupon}
          errorText={applyError}
          size="sm"
          variant="default"
        />
      )}
    </Wrapper>
  )
}
